import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { useLocale, useT } from "../i18n/LocaleProvider.js";
import { Lockup } from "./Lockup.js";
import { Ltr } from "./Bdi.js";

export function Layout({ children }: { children: ReactNode }) {
  return (
    <>
      <SkipLink />
      <Header />
      <main id="main" className="main" tabIndex={-1}>
        {children}
      </main>
      <Footer />
    </>
  );
}

function SkipLink() {
  const { m } = useT();
  return (
    <a className="skip-link" href="#main">
      {m.nav.skipToContent}
    </a>
  );
}

function Header() {
  const { m, lp, locale } = useT();
  const { switchTo } = useLocale();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === lp("/");

  const items = [
    { to: lp("/films/my-child"), label: m.nav.film },
    { to: lp("/#about"), label: m.nav.about, anchor: true },
    { to: lp("/clips"), label: m.nav.clips },
    { to: lp("/support"), label: m.nav.support },
    { to: lp("/contact"), label: m.nav.contact },
  ];

  return (
    <header className="header">
      <div className="header__inner">
        <Link to={lp("/")} className="header__brand" aria-label={m.meta.siteName}>
          <Lockup compact />
        </Link>

        <button
          type="button"
          className="header__menu"
          aria-expanded={open}
          aria-controls="site-nav"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? m.nav.close : m.nav.menu}
        </button>

        <nav id="site-nav" className={`nav ${open ? "nav--open" : ""}`} aria-label="Primary">
          <ul className="nav__list">
            {items.map((it) => (
              <li key={it.to}>
                {it.anchor ? (
                  <a className="nav__link" href={isHome ? "#about" : it.to} onClick={() => setOpen(false)}>
                    {it.label}
                  </a>
                ) : (
                  <NavLink className="nav__link" to={it.to} onClick={() => setOpen(false)}>
                    {it.label}
                  </NavLink>
                )}
              </li>
            ))}
            <li>
              <Link className="btn btn--primary nav__cta" to={lp("/share")} onClick={() => setOpen(false)}>
                {m.nav.share}
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="lang-toggle"
                lang={locale === "fa" ? "en" : "fa"}
                aria-label={m.nav.language}
                onClick={() => switchTo(locale === "fa" ? "en" : "fa")}
              >
                {m.nav.switchTo}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  const { m, lp, locale } = useT();
  const { switchTo } = useLocale();
  const year = new Intl.NumberFormat(locale === "fa" ? "fa-IR" : "en-GB", { useGrouping: false }).format(new Date().getFullYear());
  return (
    <footer className="footer">
      <div className="footer__inner">
        <div className="footer__brand">
          <Lockup compact />
          <p className="footer__note">{m.footer.dataNote}</p>
        </div>
        <ul className="footer__links">
          <li>
            <Link to={lp("/privacy")}>{m.footer.privacy}</Link>
          </li>
          <li>
            <Link to={lp("/terms")}>{m.footer.terms}</Link>
          </li>
          <li>
            <Link to={lp("/contact")}>{m.footer.contact}</Link>
          </li>
          <li>
            <a href={`mailto:${m.footer.email}`}>
              <Ltr>{m.footer.email}</Ltr>
            </a>
          </li>
          <li>
            <button type="button" className="lang-toggle" onClick={() => switchTo(locale === "fa" ? "en" : "fa")} lang={locale === "fa" ? "en" : "fa"}>
              {m.nav.switchTo}
            </button>
          </li>
        </ul>
        <p className="footer__rights">{m.footer.rights.replace("{year}", year)}</p>
      </div>
    </footer>
  );
}
